import * as React from 'react';
import styled, { css } from 'styled-components';
import { ListIterator } from '../../../utils/array';
import { TabHeaderItemsContainer } from './tab-header-items-container';
import { TabHeaderItem } from './tab-header-item';
import { ITabItemBaseProps } from '../tabs-panel';
import { ITabsPanelProps, TabStyle, TabOrientation } from '../tab-types';
import { TabNavigationButton } from './navigation';
import { selectTabsPanelTheme, isVertical, isHorizontal } from '../utils';
import { ActionButton } from '../../button';
import { Icon } from '../../icon';

export interface ITabHeaderItemsContainerProps extends
    Required<Pick<ITabsPanelProps, 'tabStyle' | 'tabOrientation' | 'tabWidth' | 'tabHeight' | 'activeTab'>>,
    Pick<ITabsPanelProps, | 'remoteHeaderContainerRef'> {
    tabOverflow: boolean;
    childCount: number;
    enableAddTab?: boolean
    tabItemData: Map<React.Key, ITabItemBaseProps>;
    tabsIterator: ListIterator<React.Key>;
    onAddTab?(): void;
    onChange(key: React.Key): void;
}

interface ITabHeaderState {
    isTabHeaderVisible: boolean;
}

const intersectionObserverOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: '0px',
    threshold: 1.0
};

const NavButtonBlock = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    ${selectTabsPanelTheme(t => css`
        border: ${t.composites.border.header} solid ${t.colors.default.tabItemBorder};
    `)}
`;

const AddButton = styled(ActionButton).attrs({
    className: String('ap-tabsPanel-addButton')
})`
    transform: translateY(3px);
    ${Icon} {
        fill: ${({ theme: { semantics: { palette } } }) => palette.themePrimary};
    }
`;

const TabHeaderItemsContainerBlock = styled.div.attrs({
    className: String('ap-tabsPanel-tabHeaderContainer')
}) <{ tabOverflow?: boolean; tabStyle: TabStyle; tabOrientation: TabOrientation; }>`
    
    display: flex;
    background-color: ${selectTabsPanelTheme(t => t.colors.tabHeaderWrapperBackground)};
    flex-direction: ${({ tabOrientation }) => (isVertical(tabOrientation) ? 'column' : 'row')};
    ${({ tabOrientation }) => isVertical(tabOrientation) && css`
        width: 150px;
    `}
    ${({ tabOrientation }) => isHorizontal(tabOrientation) && css`
        height: 45px;
    `}
    text-align: center;
    
   ${selectTabsPanelTheme((t, _isLine, { tabOverflow, tabOrientation }) => tabOverflow &&
    css`
            & ${TabHeaderItemsContainer}, & ${TabHeaderItem}{
                border-radius: 0;
            }
            & ${TabHeaderItem}:first-child{
                ${isVertical(tabOrientation) ?
            css`border-top: none;` :
            css`border-left: none;`}
            }
            & ${TabHeaderItem}:last-child{
                ${isVertical(tabOrientation) ?
            css`border-bottom: none;` :
            css`border-right: none;`}
            }
            & ${NavButtonBlock} {
                ${isVertical(tabOrientation) ?
            css`
                        max-height: 25px;
                        &:first-child{
                            border-radius: ${t.composites.borderRadius.header} 0 0 0;
                        }
                        &:last-child{
                            border-radius: 0 0 0 ${t.composites.borderRadius.header};
                        }
                    ` :
            css`
                        max-width: 25px;
                        &:first-child{
                            border-radius: ${t.composites.borderRadius.header} 0 0 0;
                        }
                        &:last-child{
                            border-radius: 0 ${t.composites.borderRadius.header} 0 0;
                        }
                    `
        }
            }
        `)
    }
`;

/**
 * Tab Header Container. This component is responsible for rendering tab header items.
 */

export class TabHeaderContainer extends React.PureComponent<ITabHeaderItemsContainerProps, ITabHeaderState> {
    private headerContainerRef = React.createRef<HTMLDivElement>();
    private observer: IntersectionObserver;

    constructor(props: ITabHeaderItemsContainerProps) {
        super(props);
        this.state = {
            isTabHeaderVisible: false
        };
        this.observer = new IntersectionObserver(this.onIntersectionChange, intersectionObserverOptions);
    }

    componentDidMount() {
        if (this.headerContainerRef.current) {
            this.observer.observe(this.headerContainerRef.current);
        }
    }

    componentWillUnmount() {
        if (this.headerContainerRef.current) {
            this.observer.unobserve(this.headerContainerRef.current);
        }
    }

    render() {
        const { tabOverflow, tabStyle, tabOrientation, enableAddTab } = this.props;
        return (
            <TabHeaderItemsContainerBlock ref={this.headerContainerRef} tabOverflow={tabOverflow} tabStyle={tabStyle} tabOrientation={tabOrientation}>
                {tabOverflow ? this.renderBackwardNavigationButton() : null}
                {this.renderTabHeader()}
                {tabOverflow ? this.renderForwardNavigationButton() : null}
                {enableAddTab ? this.renderAddTabButton() : null}
            </TabHeaderItemsContainerBlock>
        );
    }

    private renderAddTabButton() {
        const { onAddTab } = this.props;
        return <AddButton iconName="ap-add-round2.solid" iconSize="24" onClick={onAddTab} />;
    }

    private renderBackwardNavigationButton() {
        const { tabOrientation, tabStyle, tabsIterator } = this.props;
        const currentItemIndex = tabsIterator.getcurrentIndex();
        const isDisabled = currentItemIndex === 0;
        return (
            <NavButtonBlock tabStyle={tabStyle}>
                <TabNavigationButton disabled={isDisabled} iconName={isVertical(tabOrientation) ? 'ap-up-arrow-regular.outline' : 'ap-left-arrow-regular.outline'}
                    onClick={this._onPreviousButtonClick}
                />
            </NavButtonBlock>
        );
    }

    private _onPreviousButtonClick = () => {
        const { tabsIterator } = this.props;
        tabsIterator.prev();
    }

    private renderForwardNavigationButton() {
        const { tabOrientation, tabStyle, tabsIterator } = this.props;
        const currentItemIndex = tabsIterator.getcurrentIndex();
        const lastIndex = tabsIterator.getLength() - 1;
        const isDisabled = currentItemIndex === lastIndex;
        return (
            <NavButtonBlock tabStyle={tabStyle}>
                <TabNavigationButton
                    disabled={isDisabled} iconName={isVertical(tabOrientation) ? 'ap-down-arrow-regular.outline' : 'ap-right-arrow-regular.outline'}
                    onClick={this._onNextButtonClick}
                />
            </NavButtonBlock>
        );
    }

    private _onNextButtonClick = () => {
        const { tabsIterator } = this.props;
        tabsIterator.next();
    }

    private renderTabHeader() {
        const { tabStyle, tabOrientation, tabOverflow } = this.props;
        return (
            <TabHeaderItemsContainer
                tabOverflow={tabOverflow}
                tabStyle={tabStyle}
                tabOrientation={tabOrientation}>
                {this.getTabItemsToRender()}
            </TabHeaderItemsContainer>
        );
    }

    private onIntersectionChange = (entries: IntersectionObserverEntry[]) => {
        const [entry] = entries;
        this.setState({
            isTabHeaderVisible: entry.isIntersecting
        });
    }

    private getTabItemsToRender() {
        const { tabsIterator } = this.props;
        return tabsIterator.getAllValues().map(this.renderTabHeaderItem);
    }

    private renderTabHeaderItem = (key: React.Key) => {
        const { tabItemData, activeTab, ...rest } = this.props;
        const itemProps = tabItemData.get(key);
        const isActive = !!itemProps && (itemProps.id === activeTab);
        return itemProps ? (
            <TabHeaderItem
                {...itemProps}
                {...rest}
                isDisabled={itemProps.disabled}
                key={itemProps.id}
                isActive={isActive}
                isTabHeaderVisible={this.state.isTabHeaderVisible}
                onClick={this._onItemClick} />
        ) : null;
    }

    private _onItemClick = (tabKey: React.Key) => {
        const { tabsIterator } = this.props;
        tabsIterator.find(key => key === tabKey);
    }

}
